import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex, Heading,
  useDisclosure,
  VStack,
  Select,
  HStack,
  Text
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getAccounts, createAccount } from '@store/actions/libraDashboardActions';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@constants';
import { updateLibraAccountsReqData } from '@store/actions/reqDataActions';
import Pagination from '@components/Pagination';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import ConfirmModal from '@components/Modal/ConfirmModal';
import DataTable from '@components/DataTable';
import { MdAdd } from 'react-icons/md';
import { INPUT_FIELD_TYPE, INPUT_TYPE } from '@components/DynamicFormField/constant';
import { getCreateAccountFields } from './formConfigs';
import { getAccountsColumns } from './tableColumns';

export const AccountsTab = ({ selectedItem, onItemClick }) => {
  const dispatch = useDispatch();
  const { hasAuthority } = useAuthRole();
  const isBackofficeManager = hasAuthority(ROLES.BACKOFFICE_MANAGER_ROLE);

  // Redux state
  const accounts = useSelector((state) => state.libraDashboard.accounts);
  const libraConfigs = useSelector((state) => state.libraDashboard.libraConfigs);
  const createAccountState = useSelector((state) => state.libraDashboard.createAccount);
  const reqData = useSelector((state) => state.reqData.libraDashboard.accounts);

  // Local state for tracking selected accounts in sweepout config
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [formData, setFormData] = useState({});
  const [pendingFormData, setPendingFormData] = useState(null);

  // Local state for TanStack table column filters (isolated to this tab only)
  const [columnFilters, setColumnFilters] = useState([]);

  const {
    isOpen: isCreateModalOpen,
    onClose: closeCreateModal,
    onOpen: openCreateModal
  } = useDisclosure();

  const {
    isOpen: isConfirmModalOpen,
    onClose: closeConfirmModal,
    onOpen: openConfirmModal
  } = useDisclosure();

  useEffect(() => {
    fetchAccounts();
  }, [reqData.pageNumber]);

  const fetchAccounts = () => {
    const params = {
      page: reqData.pageNumber,
      page_size: reqData.pageSize
    };
    dispatch(getAccounts(params));
  };

  const handlePageChange = (page) => {
    dispatch(updateLibraAccountsReqData({
      ...reqData,
      pageNumber: page - 1
    }));
  };

  // Get unique currencies and partners from the accounts data for filter options
  const filterOptions = useMemo(() => {
    const accountsData = accounts?.data?.items || [];

    const currencies = [...new Set(accountsData.map(acc => acc.currency).filter(Boolean))];
    const partners = [...new Set(accountsData.map(acc => acc.partner).filter(Boolean))];

    return {
      currencies: currencies.sort(),
      partners: partners.sort()
    };
  }, [accounts?.data?.items]);

  // Handle filter changes
  const handleCurrencyFilterChange = (e) => {
    const value = e.target.value;
    setColumnFilters(prev => {
      const filtered = prev.filter(f => f.id !== 'currency');
      if (value) {
        return [...filtered, { id: 'currency', value }];
      }
      return filtered;
    });
  };

  const handlePartnerFilterChange = (e) => {
    const value = e.target.value;
    setColumnFilters(prev => {
      const filtered = prev.filter(f => f.id !== 'partner');
      if (value) {
        return [...filtered, { id: 'partner', value }];
      }
      return filtered;
    });
  };

  // Get current filter values for the select elements
  const currentCurrencyFilter = columnFilters.find(f => f.id === 'currency')?.value || '';
  const currentPartnerFilter = columnFilters.find(f => f.id === 'partner')?.value || '';

  // Handle field changes to track selected accounts and form data
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    if (fieldName === 'targetBankAccountIds') {
      setSelectedAccountIds(Array.isArray(value) ? value : []);
    }
  };

  // Reset state when modal closes
  const handleCloseCreateModal = () => {
    setSelectedAccountIds([]);
    setFormData({});
    closeCreateModal();
  };

  // Called when form is submitted - shows confirmation modal
  const handleFormSubmit = (formData) => {
    setPendingFormData(formData);
    closeCreateModal();
    openConfirmModal();
  };

  // Called when confirmation is confirmed
  const handleConfirmCreate = () => {
    if (!pendingFormData) return;

    const reqData = {
      uniqueAccountReference: pendingFormData.uniqueAccountReference,
      description: pendingFormData.description,
      name: pendingFormData.name,
      currency: pendingFormData.currency,
      partnerConfig: {
        partner: pendingFormData.partner,
        ...(pendingFormData.partner === 'CLEARBANK' && {
          token: pendingFormData.token,
          baseUrl: pendingFormData.baseUrl,
          privateKey: pendingFormData.privateKey,
          accountType: pendingFormData.accountType,
          legalAddress: pendingFormData.legalAddress,
          sourceBankAccountId: pendingFormData.sourceBankAccountId
        })
      },
      accountConfig: {
        sweepoutConfig: {
          enabled: pendingFormData.sweepoutEnabled || false,
          ...(pendingFormData.sweepoutEnabled && {
            fundTransferStrategy: (() => {
              const strategyType = pendingFormData.fundTransferStrategy || 'PERCENTAGE_SPLIT';

              // Parse selected account IDs (array from multi-select)
              const accountIds = Array.isArray(pendingFormData.targetBankAccountIds)
                ? pendingFormData.targetBankAccountIds
                : [];

              if (strategyType === 'PERCENTAGE_SPLIT') {
                // Create bankAccountSplits array from individual fields
                const bankAccountSplits = accountIds.map((accountId) => ({
                  bankAccountId: accountId,
                  percentage: parseFloat(pendingFormData[`percentage_${accountId}`]) || 100
                }));

                return {
                  strategyType: 'PERCENTAGE_SPLIT',
                  bankAccountSplits
                };
              } else if (strategyType === 'SKIPPABLE_RATIO') {
                // Create bankAccountRatios array from individual fields
                const bankAccountRatios = accountIds.map((accountId) => ({
                  bankAccountId: accountId,
                  ratio: parseFloat(pendingFormData[`percentage_${accountId}`]) || 1
                }));

                return {
                  strategyType: 'SKIPPABLE_RATIO',
                  bankAccountRatios
                };
              }
            })()
          })
        }
      }
    };

    dispatch(
      createAccount(reqData, () => {
        closeConfirmModal();
        setPendingFormData(null);
        setSelectedAccountIds([]);
        setFormData({});
        fetchAccounts();
      })
    );
  };

  // Called when confirmation is cancelled
  const handleCancelConfirm = () => {
    closeConfirmModal();
    setPendingFormData(null);
  };

  // Get dynamic fields with account options for sweepout configuration
  const createAccountFields = useMemo(() => {
    const baseFields = getCreateAccountFields(accounts, null, libraConfigs);

    // Find the index of the percentages field and remove it
    const fieldsWithoutPercentages = baseFields.filter(
      field => field.name !== 'percentages'
    );

    // If beneficiaries are selected, add individual percentage/ratio fields for each
    if (selectedAccountIds.length > 0) {
      const beneficiariesMap = {};
      libraConfigs?.corporateBeneficiaries?.forEach(beneficiary => {
        beneficiariesMap[beneficiary.corporateBeneficiaryId] = {
          name: beneficiary.accountName || beneficiary.label,
          id: beneficiary.corporateBeneficiaryId
        };
      });

      // Add individual fields for each selected beneficiary
      const dynamicFields = selectedAccountIds.map((beneficiaryId) => {
        const beneficiaryInfo = beneficiariesMap[beneficiaryId];
        const label = beneficiaryInfo
          ? `${beneficiaryInfo.name} (${beneficiaryInfo.id}) - Percentage/Ratio`
          : `${beneficiaryId} - Percentage/Ratio`;

        return {
          name: `percentage_${beneficiaryId}`,
          label: label,
          fieldType: INPUT_FIELD_TYPE.INPUT,
          inputType: INPUT_TYPE.NUMBER,
          isRequired: false,
          isEditable: true,
          placeholder: 'Enter percentage or ratio',
          dependsOn: { field: 'sweepoutEnabled', value: true }
        };
      });

      const allFields = [...fieldsWithoutPercentages, ...dynamicFields];

      // Filter fields based on dependsOn conditions
      return allFields.filter(field => {
        if (!field.dependsOn) return true;
        return formData[field.dependsOn.field] === field.dependsOn.value;
      });
    }

    // Filter base fields based on dependsOn conditions
    return fieldsWithoutPercentages.filter(field => {
      if (!field.dependsOn) return true;
      return formData[field.dependsOn.field] === field.dependsOn.value;
    });
  }, [accounts, libraConfigs, selectedAccountIds, formData]);

  return (
    <Box position={'relative'} overflowX={'hidden'} flex={1}>
      {/* Create Account Modal */}
      <ModalLayout
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        size="xl"
        scrollBehavior="inside"
      >
        <DynamicInputModal
          title="Create Account"
          onClose={handleCloseCreateModal}
          onSubmit={handleFormSubmit}
          fields={createAccountFields}
          isLoading={createAccountState.isLoading}
          onFieldChange={handleFieldChange}
        />
      </ModalLayout>

      {/* Confirmation Modal */}
      <ModalLayout
        isOpen={isConfirmModalOpen}
        onClose={handleCancelConfirm}
        size="md"
      >
        <ConfirmModal
          handleSubmit={handleConfirmCreate}
          isLoading={createAccountState.isLoading}
          prompt="Are you sure you want to create this account?"
        />
      </ModalLayout>

      <VStack align="stretch" spacing={4}>
        {/* Header with heading and actions */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Heading size="lg" color="colorPrimary">
            Accounts
          </Heading>
          {isBackofficeManager && (
            <Button
              leftIcon={<MdAdd />}
              colorScheme="brand"
              size="sm"
              onClick={openCreateModal}
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="lg"
              bg="brand.600"
              _hover={{ bg: 'brand.700' }}
            >
              Create Account
            </Button>
          )}
        </Flex>

        {/* Filter Controls */}
        <HStack spacing={4} flexWrap="wrap">
          <Box>
            <Text fontSize="sm" color="whiteAlpha.700" mb={2}>
              Filter by Currency
            </Text>
            <Select
              placeholder="All Currencies"
              size="sm"
              value={currentCurrencyFilter}
              onChange={handleCurrencyFilterChange}
              bg="altGray.400"
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="lg"
              color="whiteAlpha.900"
              _hover={{ borderColor: 'brand.500' }}
              _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
              minW="200px"
            >
              {filterOptions.currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </Select>
          </Box>

          <Box>
            <Text fontSize="sm" color="whiteAlpha.700" mb={2}>
              Filter by Partner
            </Text>
            <Select
              placeholder="All Partners"
              size="sm"
              value={currentPartnerFilter}
              onChange={handlePartnerFilterChange}
              bg="altGray.400"
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="lg"
              color="whiteAlpha.900"
              _hover={{ borderColor: 'brand.500' }}
              _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
              minW="200px"
            >
              {filterOptions.partners.map((partner) => (
                <option key={partner} value={partner}>
                  {partner}
                </option>
              ))}
            </Select>
          </Box>
        </HStack>

        {/* Pagination */}
        {!accounts.isLoading && accounts?.data?.items?.length > 0 && (
          <Flex mt={4}>
            <Pagination
              totalPages={accounts?.data?.maxPages || 1}
              currentPage={reqData.pageNumber + 1}
              onPageChange={handlePageChange}
            />
          </Flex>
        )}

        {/* Accounts Table */}
        <DataTable
          data={accounts?.data?.items || []}
          columns={getAccountsColumns(onItemClick, selectedItem)}
          isLoading={accounts.isLoading}
          noDataFoundMessage="No accounts found"
          onRowClick={(account) => onItemClick(account, [])}
          selectedRow={selectedItem}
          rowKeyField="accountId"
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
        />
      </VStack>
    </Box>
  );
};
